import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import ProcedurePlaceholder from "@/components/ProcedurePlaceholder";
import { HERO } from "@/data/procedureHeroImages";
import PatientTestimonials from "../components/PatientTestimonials";
import SEO from "@/components/SEO";
import { WHATSAPP_URL, TEL_URL, PHONE_DISPLAY } from "@/constants/contact";
import {
  ArrowRight,
  Award,
  ChevronRight,
  Clock,
  Footprints,
  GraduationCap,
  Layers,
  MessageCircle,
  Phone,
  Minus,
  Monitor,
  Plus,
  Sparkles,
  CircleDot,
  Droplets,
  Grid3x3,
  Activity,
  Zap,
  AlertTriangle,
} from "lucide-react";

const PAGE_URL = "https://drvickyjain.com/corn-removal-treatment-mumbai";
const LIPOMA_PATH = "/lipoma-removal-surgery-mumbai";
const CYST_PATH = "/sebaceous-cyst-removal-mumbai";
const MOLE_PATH = "/mole-removal-mumbai";

const CORN_FAQS = [
  {
    question: "Do corn caps permanently remove corns?",
    answer:
      "No. Corn caps containing salicylic acid soften and dissolve the thickened keratin of a corn temporarily, making it easier to pare away the top layers. They do not remove the corn's central core and do not address the pressure cause creating the corn. The corn returns after the salicylic acid treatment stops if the footwear and mechanical cause are not changed. Corn caps provide temporary pain relief, not permanent removal.",
  },
  {
    question: "Can I remove a corn at home?",
    answer:
      "Home paring of a corn with a pumice stone or emery board can reduce the thickness of the overlying keratin and provide temporary relief. Attempting to cut or dig out the corn with sharp instruments at home risks cutting healthy skin, causing bleeding, and introducing infection. This risk is significantly higher in diabetic patients and those with poor circulation. Clinical removal by a qualified professional is safer and more effective than home attempts.",
  },
  {
    question: "Why does my corn keep coming back?",
    answer:
      "Corns recur when the underlying pressure cause is not addressed. Removing the corn without modifying the footwear, correcting the foot alignment, or addressing a bony prominence leaves the same mechanical force acting on the same area of skin. The skin responds by rebuilding the thickened keratin. Permanent relief requires both corn removal and elimination of the cause. For patients with recurrent corns despite footwear modification, an underlying bony abnormality is likely the primary cause and surgical correction is the appropriate permanent solution.",
  },
  {
    question: "Is corn removal painful?",
    answer:
      "Standard clinical paring of a corn is performed without anesthesia and most patients describe it as mild pressure rather than pain, particularly because the thickened corn tissue itself has reduced sensitivity compared to normal skin. Surgical excision under local anesthesia is painless during the procedure. The anesthesia injection produces brief discomfort. Post-procedure tenderness for 1 to 2 days is managed with standard pain relief.",
  },
  {
    question: "How long does corn removal take?",
    answer:
      "Standard clinical paring takes 15 to 30 minutes. Surgical excision takes 20 to 40 minutes depending on the complexity. Both are daycare procedures with no hospital admission required. Patients resume normal activity the same day for clinical paring and within 2 to 3 days for surgical cases.",
  },
  {
    question: "Can diabetic patients have corn removal?",
    answer:
      "Yes, but with specific precautions appropriate to the diabetic condition. Diabetic patients must not self-treat and must not use corn plasters or salicylic acid preparations. Clinical and surgical corn removal in diabetic patients is performed with careful attention to wound management and healing. Dr. Jain manages diabetic foot corns with the additional precautions these patients require.",
  },
  {
    question: "What is the difference between a corn and a plantar wart?",
    answer:
      "A corn is thickened skin with a central core caused by pressure over a bony area. A plantar wart is a viral skin infection caused by HPV that develops on the sole of the foot. Both can be painful and appear as thickened skin. The distinction is important because the treatments are different. A plantar wart has small black dots within it, representing thrombosed capillaries, which are not present in a corn. A corn's skin lines continue through the lesion while a wart disrupts the normal skin line pattern. Dermoscopy or clinical assessment distinguishes between them when there is uncertainty.",
  },
  {
    question: "Will I need to take time off work for corn removal?",
    answer:
      "For standard clinical paring, no time off work is required. Patients resume normal activity immediately. For surgical excision, most patients with desk jobs return to work the following day. Patients whose work involves prolonged standing or walking may need 2 to 3 days before returning to full duty. Dr. Jain advises on specific return-to-work timing based on the procedure performed and the patient's work requirements.",
  },
];

const structuredData = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "MedicalCondition",
      name: "Foot Corn",
      alternateName: ["Corn", "Heloma"],
      description:
        "Localized keratin thickening with a central core over a bony foot prominence caused by pressure and friction; managed with conservative care, clinical paring, or surgical excision when indicated.",
      associatedAnatomy: { "@type": "AnatomicalStructure", name: "Foot" },
      possibleTreatment: ["Conservative management", "Clinical paring and removal", "Surgical excision"],
      url: PAGE_URL,
    },
    {
      "@type": "MedicalProcedure",
      name: "Corn surgical excision",
      description:
        "Local anesthesia removal of recurrent or deep foot corns with correction of contributing bony prominence when needed, under plastic surgical care.",
      procedureType: "SurgicalProcedure",
      bodyLocation: { "@type": "AnatomicalStructure", name: "Foot" },
      medicalSpecialty: "https://schema.org/PlasticSurgery",
      url: PAGE_URL,
    },
    {
      "@type": "FAQPage",
      mainEntity: CORN_FAQS.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: { "@type": "Answer", text: faq.answer },
      })),
    },
  ],
};

function CornJsonLd() {
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />;
}

function Breadcrumb() {
  const items = [
    { name: "Home", href: "/" },
    { name: "Reconstructive Surgery", href: "/reconstructive-surgery-in-mumbai" },
    { name: "Corn Removal Treatment", href: "/corn-removal-treatment-mumbai" },
  ];
  return (
    <nav className="bg-warm-white pt-24 pb-4" data-testid="breadcrumb">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <ol className="flex flex-wrap items-center gap-2 text-sm text-deep-navy/60">
          {items.map((item, index) => (
            <li key={item.name} className="flex items-center gap-2">
              {index > 0 && <ChevronRight className="w-3 h-3 text-gold" />}
              {index === items.length - 1 ? (
                <span className="text-deep-navy/70">{item.name}</span>
              ) : (
                <Link to={item.href} className="hover:text-gold transition-colors">
                  {item.name}
                </Link>
              )}
            </li>
          ))}
        </ol>
      </div>
    </nav>
  );
}

function HeroSection() {
  return (
    <section className="bg-warm-white pt-8 md:pt-10 pb-12 md:pb-20" data-testid="procedure-hero">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Foot Care</p>
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl text-premium-purple leading-[1.08] mb-4">Corn Removal Treatment</h1>
            <div className="w-12 h-[2px] bg-gold mb-5" />
            <p className="text-lg sm:text-xl text-deep-navy/65 leading-relaxed mb-7 max-w-xl font-light">
              Permanent Relief from Foot Corns by Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon
            </p>
            <div className="flex flex-wrap gap-2">
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-semibold text-premium-purple">Board Certified</span>
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-semibold text-premium-purple">17+ Years Experience</span>
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-semibold text-premium-purple">International Training</span>
            </div>
          </div>
          <div className="flex justify-center lg:justify-end">
            <ProcedurePlaceholder
              procedureName="Corn Removal"
              icon="🦶"
              size="hero"
              imageSrc={HERO.cornRemoval}
              imageAlt="Foot corn and callus care"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function WhatIsSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20" data-testid="what-is-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Overview</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-6">What is a Foot Corn?</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              A corn is a localized area of thickened skin that develops in response to repeated pressure or friction over a bony prominence of the foot. The body produces this thickening as a protective response, but as the skin builds up it develops a hard central core that presses into the underlying tissue with every step, causing pain. Unlike a callus, which is diffuse thickened skin without a central core, a corn has a defined, cone-shaped core that is the direct source of the pain.
            </p>
            <p>
              Corns are not dangerous in healthy patients but they cause significant functional impairment. Patients with painful corns modify their gait to avoid pressure on the affected area, which leads to secondary problems including joint and back pain from altered walking mechanics. Corns do not resolve on their own. The thickened skin and the underlying pressure cause will continue producing the corn indefinitely without treatment.
            </p>
            <p>
              Dr. Vicky Jain treats corns using clinical removal, surgical excision for recurrent or deep cases, and advice on addressing the underlying cause to prevent recurrence. Permanent relief requires removing the corn and modifying the pressure cause. Removing the corn without addressing the cause produces temporary improvement followed by recurrence.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function DrJainApproachSection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="dr-jain-approach-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Our Approach</p>
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-8">Dr. Jain&apos;s Approach to Corn Treatment</h2>
            <div className="space-y-6 text-white/75 leading-relaxed">
              <p>
                The most important distinction in corn treatment is between temporary symptom relief and permanent resolution. Corn plasters, salicylic acid preparations, and paring of the thickened skin all reduce pain temporarily by removing the built-up keratin, but they do not address the bony prominence or pressure point that is causing the skin to thicken. Without correcting the mechanical cause, the corn returns after every treatment cycle.
              </p>
              <p>
                Dr. Jain assesses the cause of each corn before recommending treatment: the footwear being worn, the foot&apos;s structural alignment, whether any underlying bony prominence is contributing, and whether the patient has conditions such as diabetes that require a more cautious approach. For straightforward corns caused by footwear, clinical removal combined with footwear modification produces long-lasting results. For recurrent corns where an underlying bony abnormality is the primary cause, surgical correction of the bony prominence alongside corn removal is the appropriate permanent solution.
              </p>
            </div>
          </div>
          <div className="relative">
            <img src="/dr-jain.avif" alt="Dr. Vicky Jain" loading="lazy" decoding="async" className="w-full h-[420px] object-cover object-top" />
            <div
              className="absolute top-0 left-0 right-0 h-[420px] pointer-events-none hidden lg:block"
              style={{ background: "linear-gradient(to right, #3D2566 0%, rgba(61, 37, 102, 0) 15%)" }}
            />
            <div className="mt-4 text-center lg:text-left">
              <p className="font-serif text-lg text-white/80">Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function TypesOfCornsSection() {
  const cards = [
    {
      title: "Hard Corn",
      icon: CircleDot,
      copy: "The most common type. Found on the tops and sides of toes and over other bony prominences. Has a firm, well-defined central core. Caused by pressure from footwear against a bony area. Painful when direct pressure is applied.",
    },
    {
      title: "Soft Corn",
      icon: Droplets,
      copy: "Found between the toes, most commonly between the fourth and fifth toes. Appears whitish and moist due to the moisture trapped between the toes. Caused by friction between adjacent toe bones. Can be particularly painful and prone to secondary infection due to the moist environment.",
    },
    {
      title: "Seed Corn",
      icon: Grid3x3,
      copy: "Small, discrete clusters of corns found on the sole of the foot. Less common than hard corns. Cause a sensation similar to walking on a small stone. Associated with very dry skin and localized pressure points on the plantar surface.",
    },
    {
      title: "Vascular Corn",
      icon: Activity,
      copy: "A corn that has developed a blood supply within its structure due to longstanding presence. Removal requires more care than standard corn removal as the blood supply creates bleeding during paring. Requires clinical management rather than self-treatment.",
    },
    {
      title: "Fibrous Corn",
      icon: Layers,
      copy: "A longstanding corn where the tissue has become more fibrous and firmly attached to the underlying tissue. More difficult to remove than a standard hard corn and more likely to require surgical excision rather than simple paring.",
    },
    {
      title: "Neurovascular Corn",
      icon: Zap,
      copy: "A corn with nerve and blood vessel involvement. Produces pain disproportionate to its size. Requires specialist assessment and surgical removal rather than conservative paring. Most commonly found on the sole of the foot.",
    },
  ];
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="types-of-corns-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Types</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Types of Foot Corns</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {cards.map((card) => (
            <div key={card.title} className="bg-white p-8 border border-[#E8E2D9]">
              <div className="w-12 h-12 bg-gold/10 flex items-center justify-center mb-4" aria-hidden>
                <card.icon className="w-6 h-6 text-gold" />
              </div>
              <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{card.title}</h3>
              <p className="text-deep-navy/70 leading-relaxed">{card.copy}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const VS_CALLUS_ROWS = [
  {
    label: "Central Core",
    corn: "Present. A well-defined cone-shaped core presses into underlying tissue.",
    callus: "Absent. Thickened skin is diffuse without a defined focal point.",
  },
  {
    label: "Pain",
    corn: "Painful, particularly with direct pressure. The core is the source of the pain.",
    callus: "Usually painless or mildly uncomfortable. Does not have a pressure-creating core.",
  },
  {
    label: "Size and Shape",
    corn: "Small and circular with a defined border.",
    callus: "Larger, diffuse, and irregularly shaped.",
  },
  {
    label: "Location",
    corn: "Over bony prominences on the toes, between toes, or on the sole.",
    callus: "Broader areas of the sole and heel subject to general pressure.",
  },
  {
    label: "Treatment",
    corn: "Requires removal of the core and correction of the pressure cause.",
    callus: "Often responds to moisturizing, pumice stone, and footwear modification without clinical intervention.",
  },
];

function CornVsCallusSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="corn-vs-callus-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Understanding Your Options</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Corn vs. Callus: Understanding the Difference</h2>
        <div className="max-w-5xl mx-auto overflow-x-auto">
          <table className="w-full text-left border-collapse text-sm md:text-base">
            <thead>
              <tr className="border-b border-deep-navy/20">
                <th scope="col" className="py-4 pr-4 font-serif text-premium-purple font-normal w-[18%]" />
                <th scope="col" className="py-4 px-3 font-serif text-premium-purple bg-white/80">
                  Corn
                </th>
                <th scope="col" className="py-4 pl-3 font-serif text-premium-purple bg-white/80">
                  Callus
                </th>
              </tr>
            </thead>
            <tbody>
              {VS_CALLUS_ROWS.map((row) => (
                <tr key={row.label} className="border-b border-deep-navy/10">
                  <th scope="row" className="py-4 pr-4 font-medium text-premium-purple align-top">
                    {row.label}
                  </th>
                  <td className="py-4 px-3 text-deep-navy/70 align-top">{row.corn}</td>
                  <td className="py-4 pl-3 text-deep-navy/70 align-top">{row.callus}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="max-w-5xl mx-auto mt-8 text-deep-navy/70 text-center md:text-left leading-relaxed">
          Both corns and calluses are caused by repeated pressure and friction. The distinction matters because corns cause pain and require treatment while calluses are often protective and require only symptomatic management. A painful area of thickened skin on the foot with a defined central point is almost certainly a corn and warrants assessment.
        </p>
      </div>
    </section>
  );
}

function CausesPreventionSection() {
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="causes-prevention-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Causes & Prevention</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-6">What Causes Corns and How to Prevent Recurrence</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Corns develop wherever repeated pressure or friction acts against the skin over a bony area. The most common cause is footwear that does not fit correctly: shoes that are too tight create pressure on the tops and sides of the toes, shoes with high heels shift body weight onto the front of the foot and create pressure on the toe joints, and shoes with rough internal seams create friction against specific areas. Other causes include foot deformities such as hammertoe or bunion that create abnormal pressure points, and walking barefoot on hard surfaces that increases plantar pressure.
            </p>
            <p>
              Prevention requires addressing the mechanical cause. Shoes with adequate width in the toe box, cushioned soles, and low heels significantly reduce the formation of new corns after treatment. Protective padding over known pressure points reduces friction. Orthotics redistribute plantar pressure in patients with structural foot abnormalities. For patients with bony prominences that create persistent pressure points regardless of footwear, surgical correction of the bony cause is the only permanent prevention strategy.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

const TREATMENT_OPTIONS = [
  {
    title: "Conservative Management",
    body:
      "Appropriate for early, mild corns in patients without diabetes or circulatory conditions. Salicylic acid preparations soften the thickened keratin over repeated applications, making the corn easier to pare. Corn plasters provide cushioning that reduces pressure and pain while the conservative treatment takes effect. These approaches provide temporary improvement but do not remove the corn permanently and do not address the underlying cause. Recurrence is expected if the pressure cause is not modified.",
  },
  {
    title: "Clinical Paring and Removal",
    body:
      "A clinic procedure where the corn is carefully removed using sterile instruments under controlled conditions. The thickened keratin and the central core are debrided back to normal skin level. Provides immediate pain relief in a single session. No anesthesia is typically required for standard paring. The procedure takes 15 to 30 minutes. Recurrence is likely if the footwear and pressure cause are not addressed alongside the clinical removal.",
  },
  {
    title: "Surgical Excision",
    body:
      "For recurrent corns, deep corns, or corns associated with an underlying bony prominence, surgical excision under local anesthesia removes the corn and addresses the contributing structural cause. The bony prominence causing the recurrent pressure point is reduced surgically alongside corn removal. This is the most definitive treatment for corns that have recurred despite conservative management and footwear modification. Results are permanent when the mechanical cause is fully corrected.",
  },
];

function TreatmentOptionsSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="treatment-options-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Procedure Options</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Corn Removal Treatment Options</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {TREATMENT_OPTIONS.map((item) => (
            <div key={item.title} className="bg-white p-8 border border-[#E8E2D9]">
              <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{item.title}</h3>
              <p className="text-deep-navy/70 leading-relaxed">{item.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowProcedureSection() {
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="how-procedure-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">The Procedure</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-6">How Corn Removal is Performed</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Clinical paring takes 15 to 30 minutes. No anesthesia is required for most standard corn removal. The foot is cleaned and the corn is carefully debrided using sterile instruments. The central core is removed to the level of normal surrounding skin. A simple dressing is applied. Patients walk out of the clinic with immediate pain relief in most cases.
            </p>
            <p>
              Surgical excision under local anesthesia is used for deep, recurrent, or fibrous corns and for cases where the underlying bony prominence is being corrected simultaneously. The anesthesia injection to the foot produces brief discomfort. The corn and any contributing bony abnormality are addressed through a small incision. The wound is closed with fine sutures and a dressing is applied. Patients resume normal walking within 2 to 3 days in most cases.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function RecoveryTimelineSection() {
  const milestones = [
    {
      period: "Same Day",
      description:
        "Immediate pain relief after corn removal in most cases. Patients walk out of the clinic with significantly reduced discomfort. A simple dressing protects the treated area.",
      icon: Sparkles,
    },
    {
      period: "Days 2 to 3",
      description:
        "Normal walking resumes. The treated area is tender but functional. Comfortable, well-fitted shoes are worn during this period.",
      icon: Footprints,
    },
    {
      period: "Week 1",
      description:
        "Complete healing for standard clinical paring. Sutures removed for surgical excision cases. The skin surface at the treated site is smooth and normal.",
      icon: Monitor,
    },
    {
      period: "Weeks 2 to 4",
      description:
        "Full recovery for surgical cases. The site is fully healed. Footwear modification and any prescribed orthotics are in use to prevent recurrence.",
      icon: Clock,
    },
  ];
  return (
    <section className="bg-premium-purple py-14 md:py-20" data-testid="recovery-timeline-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">What to Expect</p>
        <h2 className="font-serif text-3xl md:text-4xl text-white mb-12 text-center">Recovery Timeline</h2>
        <div className="hidden md:block relative">
          <div className="absolute top-16 left-0 right-0 h-0.5 bg-deep-navy/10" />
          <div className="grid grid-cols-4 gap-4">
            {milestones.map((milestone, index) => (
              <div key={milestone.period} className="relative" data-testid={`timeline-milestone-${index}`}>
                <div className="flex justify-center mb-3">
                  <milestone.icon className="w-6 h-6 text-deep-navy/40" />
                </div>
                <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center mx-auto mb-4 relative z-10">
                  <div className="w-3 h-3 rounded-full bg-warm-white" />
                </div>
                <div className="text-center">
                  <h4 className="font-serif text-lg text-premium-purple mb-2">{milestone.period}</h4>
                  <p className="text-sm text-deep-navy/60 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="md:hidden relative pl-12">
          <div className="absolute top-0 bottom-0 left-4 w-0.5 bg-deep-navy/10" />
          <div className="space-y-8">
            {milestones.map((milestone, index) => (
              <div key={milestone.period} className="relative" data-testid={`timeline-mobile-${index}`}>
                <div className="absolute -left-12 top-0 flex flex-col items-center">
                  <milestone.icon className="w-5 h-5 text-deep-navy/40 mb-1" />
                  <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center">
                    <div className="w-3 h-3 rounded-full bg-warm-white" />
                  </div>
                </div>
                <div className="pl-4">
                  <h4 className="font-serif text-lg text-premium-purple mb-2">{milestone.period}</h4>
                  <p className="text-sm text-deep-navy/60 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function DiabeticPatientsSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="diabetic-patients-section" aria-labelledby="diabetic-heading">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Important Note</p>
        <h2 id="diabetic-heading" className="font-serif text-3xl md:text-4xl text-premium-purple mb-8 text-center">
          Important: Corn Treatment for Diabetic Patients
        </h2>
        <Alert className="rounded-none border-2 border-gold/50 bg-white max-w-4xl mx-auto py-6 px-4 md:px-6">
          <AlertTriangle className="h-5 w-5 text-gold" aria-hidden />
          <AlertTitle className="font-serif text-lg text-premium-purple pl-7 mb-2">Do not self-treat foot corns if you have diabetes</AlertTitle>
          <AlertDescription className="pl-7 text-deep-navy/80 space-y-6">
            <p className="leading-relaxed">
              Diabetic patients must not self-treat foot corns under any circumstances. Diabetes causes peripheral neuropathy, which reduces pain sensation in the feet, and peripheral vascular disease, which reduces blood supply and impairs wound healing. A diabetic patient may injure the foot significantly during self-treatment without feeling the damage, and any wound, however small, heals slowly and is at high risk of infection that can progress to serious complications.
            </p>
            <p className="leading-relaxed">
              Corn plasters and salicylic acid preparations are specifically contraindicated in diabetic patients. The acid affects surrounding healthy skin alongside the corn and can cause chemical burns that do not heal normally in diabetic tissue. All diabetic patients with foot corns should be assessed and treated by a qualified specialist who understands the specific risks and precautions required. Dr. Jain manages foot corns in diabetic patients with appropriate precautions specific to their condition.
            </p>
          </AlertDescription>
        </Alert>
      </div>
    </section>
  );
}

function WhoShouldSeekSection() {
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="who-should-seek-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Am I a Candidate?</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-6">Who Should Seek Corn Removal Treatment?</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Any patient experiencing foot pain from a corn that is interfering with walking, exercise, or daily activity is appropriate for consultation. There is no minimum severity. A small corn that causes significant pain with certain footwear is as valid an indication for treatment as a large, longstanding corn that causes constant discomfort. Earlier treatment produces a smaller, simpler removal and reduces the time the patient spends in pain.
            </p>
            <p>
              Patients with recurrent corns that have been treated repeatedly with conservative measures and continue to return despite footwear modification should seek assessment for the underlying structural cause rather than continuing a cycle of temporary relief. Patients with diabetes, poor circulation, or any condition affecting foot sensation or healing should seek specialist assessment for any foot concern including corns rather than attempting self-treatment. These patients require a more careful approach than healthy adults with straightforward corns.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function PatientResultsSection() {
  return <PatientTestimonials procedureName="Lipoma" />;
}

function FAQSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="faq-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Common Questions</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Frequently Asked Questions</h2>
        <div className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="w-full">
            {CORN_FAQS.map((faq, index) => (
              <AccordionItem key={faq.question} value={`corn-${index}`} className="border-b border-deep-navy/10">
                <AccordionTrigger className="text-left font-serif text-lg text-premium-purple hover:no-underline hover:text-gold py-6 [&[data-state=open]>div>svg:first-child]:hidden [&[data-state=open]>div>svg:last-child]:block [&>svg]:hidden">
                  <span className="pr-4">{faq.question}</span>
                  <div className="flex-shrink-0">
                    <Plus className="w-5 h-5 text-gold" />
                    <Minus className="w-5 h-5 text-gold hidden" />
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-deep-navy/70 leading-relaxed pb-6">{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}

function ConsultationCTASection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="consultation-cta-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center">
        <h2 className="font-serif text-3xl md:text-4xl text-warm-white mb-4">Book Your Corn Removal Consultation</h2>
        <p className="text-warm-white/70 mb-8 max-w-2xl mx-auto leading-relaxed">
          During your consultation, Dr. Jain assesses the corn type, severity, and underlying cause to determine whether clinical removal, surgical excision, or a combination with footwear and structural correction provides the most lasting result. Diabetic patients receive a management approach specific to their condition. You leave with a clear treatment plan and practical guidance on preventing recurrence.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
          <Button
            asChild
            className="bg-gold text-white hover:bg-gold-hover border-none rounded-none px-8 py-6 text-base"
            data-testid="cta-book-consultation"
          >
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
              Book Consultation
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
          </Button>
          <Button
          asChild
          variant="outline"
          className="border-warm-white text-warm-white hover:bg-warm-white hover:text-deep-navy rounded-none px-8 py-6 text-base"
          data-testid="cta-call"
        >
          <a href={TEL_URL}>
            <Phone className="w-5 h-5 mr-2" />
            Call {PHONE_DISPLAY}
          </a>
        </Button>
        </div>
      </div>
    </section>
  );
}

function RelatedProceduresSection() {
  const procedures = [
    {
      name: "Lipoma Removal",
      description: "Removal of benign tissue growths assessed alongside foot and skin concerns.",
      href: LIPOMA_PATH,
    },
    {
      name: "Sebaceous Cyst Removal",
      description: "Removal of skin cysts that occasionally develop on the foot alongside corn concerns.",
      href: CYST_PATH,
    },
    {
      name: "Mole Removal",
      description: "Assessment and removal of skin lesions on the foot that require evaluation alongside corn treatment.",
      href: MOLE_PATH,
    },
  ];
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="related-procedures-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Related Procedures</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Related Procedures</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {procedures.map((procedure) => (
            <div
              key={procedure.name}
              className="bg-white p-8 border border-[#E8E2D9] hover:shadow-lg transition-shadow duration-300 group"
            >
              <h3 className="font-serif text-xl text-premium-purple mb-3 font-semibold tracking-tight">{procedure.name}</h3>
              <p className="text-deep-navy/60 mb-6 leading-relaxed">{procedure.description}</p>
              <Link
                to={procedure.href}
                className="inline-flex items-center text-deep-navy font-medium hover:text-gold transition-colors"
              >
                Learn More →
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function CornRemovalPage() {
  return (
    <>
      <SEO
        title="Corn Removal Treatment | Dr. Vicky Jain"
        description="Painful foot corns causing difficulty walking. Dr. Vicky Jain provides permanent corn removal treatment for all corn types. Book your consultation today."
        canonical="/corn-removal-treatment-mumbai"
      />
      <CornJsonLd />
      <Breadcrumb />
      <HeroSection />
      <WhatIsSection />
      <DrJainApproachSection />
      <TypesOfCornsSection />
      <CornVsCallusSection />
      <CausesPreventionSection />
      <TreatmentOptionsSection />
      <HowProcedureSection />
      <RecoveryTimelineSection />
      <DiabeticPatientsSection />
      <WhoShouldSeekSection />
      <PatientResultsSection />
      <FAQSection />
      <ConsultationCTASection />
      <RelatedProceduresSection />
    </>
  );
}
